import { Tab } from '../_types/tab.ts';
import { KeyboardEvent, useEffect, useRef } from 'react';
import { getKeys } from '../../../../_helpers/keyboard.ts';
import { useComponentContext } from '../_helpers/componentContext.tsx';
import classNames from 'classnames';

import './Tabs.scss';

export interface TabPanelAttributes {
	role: 'tabpanel';
	tabIndex: number;
	'aria-labelledby': string;
	id: string;
}

export interface TabsProps {
	className?: string;
	tabList: Tab[];
	selectedTabIndex: number;
	tabChange: (selectedTabIndex: number) => void;
}

const Tabs: React.FC<TabsProps> = ({
	className,
	tabList,
	selectedTabIndex,
	tabChange,
}) => {
	const componentId = useComponentContext().componentId;
	const buttonRefs = useRef<HTMLButtonElement[]>([]);

	useEffect(() => {
		if (tabList.length > 1) {
			setTabIndex(selectedTabIndex);
		}
	}, []);

	const onTabButtonClicked = (
		event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		tabIndex: number
	) => {
		event.stopPropagation();
		if (selectedTabIndex != tabIndex) {
			setTabIndex(tabIndex);
		}
	};

	const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
		const keys = getKeys(event.nativeEvent);
		const selectPrevious = keys.isLeft && selectedTabIndex !== 0;
		const selectNext = keys.isRight && selectedTabIndex !== tabList.length - 1;
		const index = selectPrevious
			? selectedTabIndex - 1
			: selectNext
				? selectedTabIndex + 1
				: undefined;
		if (index !== undefined) {
			setTabIndex(index);
			buttonRefs.current[index]?.focus();
		}
	};

	const setTabIndex = (tabIndex: number) => {
		tabChange(tabIndex);
	};

	return (
		<>
			{tabList.length > 1 && (
				<div
					className={classNames('blk-ac-tabs', className)}
					role="tablist"
					onKeyDown={(event) => onKeyDown(event)}
				>
					{Array.isArray(tabList) &&
						!!tabList.length &&
						tabList.map((tab, index) => {
							const isSelected = index === selectedTabIndex;
							return (
								<button
									role="tab"
									aria-selected={isSelected ? 'true' : 'false'}
									ref={(ref) =>
										ref
											? (buttonRefs.current[index] = ref)
											: delete buttonRefs.current[index]
									}
									aria-controls={getTabPanelId(componentId, index)}
									id={getTabId(componentId, index)}
									tabIndex={isSelected ? 0 : -1}
									key={index}
									onClick={(
										e: React.MouseEvent<HTMLButtonElement, MouseEvent>
									) => onTabButtonClicked(e, index)}
									className={`${
										isSelected
											? 'selected-tab blk-ac-tab-m-selected'
											: 'blk-ac-tab-m'
									} tab`}
									aria-label={tab.tabAriaLabel || tab.tabLabel}
									data-link-event={tab.tabDataLinkEvent}
									data-label={tab.tabLabel}
								>
									{tab.tabLabel}
								</button>
							);
						})}
					<div className="key-line-placeholder"></div>
				</div>
			)}
		</>
	);
};

export default Tabs;

const getTabId = (id: string, tabIndex: number) => {
	return id + '-tab-' + tabIndex.toString();
};

const getTabPanelId = (id: string, tabIndex: number) => {
	return id + '-tabpanel-' + tabIndex.toString();
};

export const getTabPanelAttributes = (
	componentId: string,
	tabPanelIndex: number,
	areMultipleTabs: boolean | undefined
): TabPanelAttributes | undefined => {
	return areMultipleTabs
		? {
				role: 'tabpanel',
				tabIndex: 0, //HTML attribute that makes the element focusable
				'aria-labelledby': getTabId(componentId, tabPanelIndex),
				id: getTabPanelId(componentId, tabPanelIndex),
			}
		: undefined;
};
